import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { UserPersona } from '@/types/persona';
import { prefixKey } from '@/utils/storagePrefix';

interface PersonaContextType {
  currentPersona: UserPersona;
  setCurrentPersona: (persona: UserPersona) => void;
  isManager: boolean;
  availablePersonas: UserPersona[];
}

const PersonaContext = createContext<PersonaContextType | undefined>(undefined);

const PERSONA_STORAGE_KEY = prefixKey('user-selected-persona');

// Manager homepage is hidden for R1, so personal is the only selectable persona
const AVAILABLE_PERSONAS: UserPersona[] = ['personal'];

interface PersonaProviderProps {
  children: ReactNode;
  isManager?: boolean; // Determines if user has manager access
}

export const PersonaProvider: React.FC<PersonaProviderProps> = ({ 
  children, 
  isManager = true // For demo purposes, all users are managers
}) => {
  const availablePersonas = AVAILABLE_PERSONAS;

  // Load saved persona from localStorage or default to 'personal'
  const [currentPersona, setCurrentPersonaState] = useState<UserPersona>(() => {
    try {
      const saved = localStorage.getItem(PERSONA_STORAGE_KEY);
      if (saved && availablePersonas.includes(saved as UserPersona)) {
        return saved as UserPersona;
      }
    } catch (error) {
      console.error('Failed to load saved persona:', error);
    }
    return 'personal'; // Always default to personal
  });

  // Persist persona selection to localStorage
  const setCurrentPersona = (persona: UserPersona) => {
    if (!availablePersonas.includes(persona)) {
      console.warn(`Persona "${persona}" is not available for this user`);
      return;
    }
    
    setCurrentPersonaState(persona);
    try {
      localStorage.setItem(PERSONA_STORAGE_KEY, persona);
    } catch (error) {
      console.error('Failed to save persona preference:', error);
    }
  };

  // Ensure current persona is valid when availablePersonas changes
  useEffect(() => {
    if (!availablePersonas.includes(currentPersona)) {
      setCurrentPersona('personal');
    }
  }, [availablePersonas, currentPersona]);

  return (
    <PersonaContext.Provider
      value={{
        currentPersona,
        setCurrentPersona,
        isManager,
        availablePersonas,
      }}
    >
      {children}
    </PersonaContext.Provider>
  );
};

export const usePersona = () => {
  const context = useContext(PersonaContext);
  if (!context) {
    throw new Error('usePersona must be used within PersonaProvider');
  }
  return context;
};
